// LandingDashboard.tsx

import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  LinearProgress,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import { Speed, AssignmentTurnedIn, PlayArrow, CheckCircle, ArrowForward, Refresh, School } from "@mui/icons-material";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import quizApi from "@/services/quizService";
import type { QuizHistoryResponse, QuizUserSummaryResponse } from "@/types/Quiz.types";

// interface QuizAttempt {
//   submissionId: string;
//   quizName: string;
//   paper: string;
//   scorePercentage: number;
//   totalQuestions: number;
//   correctCount: number;
//   timeConsumedMin: number;
//   avgPaceMin: number;
//   createdAt: string;
//   complianceScores: {
//     hkmaBO: number;
//     amlo: number;
//     osco: number;
//   };
// }

// const mockDashboardData: QuizAttempt[] = [
//   {
//     submissionId: "6f376986-1aa8-4805-82bc-55b342aa3718",
//     quizName: "hkmaaml",
//     paper: "HKMA AML CFT Guidelines",
//     scorePercentage: 80,
//     totalQuestions: 5,
//     correctCount: 4,
//     timeConsumedMin: 4,
//     avgPaceMin: 0.8,
//     createdAt: "2026-06-30T02:56:31.543Z",
//     complianceScores: {
//       hkmaBO: 100,
//       amlo: 40,
//       osco: 60,
//     },
//   },
// ];

export const LandingDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // const [activeAttempt] = useState<QuizAttempt>(mockDashboardData[0]);
  const [history30d, setHistory30d] = useState<QuizHistoryResponse | null>();
  const [userSummary, setUserSummary] = useState<QuizUserSummaryResponse | null>();

  useEffect(() => {
    quizApi.getQuizUserSummary().then((data) => {
      setUserSummary(data);
    });
    quizApi.getQuizPast30DaysHistory().then((data) => {
      setHistory30d(data);
    });
  }, []);

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh", mt: 8, py: 4, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl" disableGutters>
        {/* 1. WELCOME HERO BANNER */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 1,
            background: isDark
              ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
            color: "text.primary",
            position: "relative",
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -80,
              right: -80,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, rgba(0,0,0,0) 70%)`,
            }}
          />

          <Grid container sx={{ alignItems: "center", gap: 3 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack sx={{ flexDirection: "row", gap: 1, mb: 1.5, alignItems: "center" }}>
                <Chip
                  label={t("pages.landingDashboard.hero.activeSession")}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    color: "primary.main",
                    fontWeight: 700,
                    borderRadius: 1,
                  }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("pages.landingDashboard.hero.lastSynced")}
                </Typography>
              </Stack>

              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: "-0.02em", mb: 1, color: "text.primary" }}>
                {t("pages.landingDashboard.hero.welcomeTitle")}
              </Typography>

              <Typography variant="body1" sx={{ color: "text.secondary", mb: 3, maxWidth: 580 }}>
                <Trans
                  i18nKey="pages.landingDashboard.hero.welcomeDescription"
                  values={{ count: userSummary?.totalCompleted ?? 0 }}
                  components={{ bold: <strong /> }}
                />
              </Typography>

              <Stack sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={() => navigate("/revision/engine")}
                  sx={{
                    bgcolor: "primary.main",
                    "&:hover": { bgcolor: "primary.dark" },
                    borderRadius: 1,
                    px: 3,
                    py: 1.2,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  {t("pages.landingDashboard.hero.btnResume")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/revision/history")}
                  sx={{
                    color: "text.primary",
                    borderColor: "divider",
                    "&:hover": { borderColor: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.05) },
                    borderRadius: 1,
                    px: 3,
                    py: 1.2,
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  {t("pages.landingDashboard.hero.btnHistory")}
                </Button>
              </Stack>
            </Grid>

            {/* Quick Stat Counter Badges inside Hero */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Grid container sx={{ gap: 2 }}>
                <Grid size={{ xs: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: isDark ? alpha(theme.palette.background.default, 0.6) : "background.paper",
                      backdropFilter: "blur(10px)",
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      {t("pages.landingDashboard.hero.streakLabel")}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "warning.main", mt: 0.5 }}>
                      {t("pages.landingDashboard.hero.streakValue", { days: userSummary?.streakDays ?? 0 })}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: isDark ? alpha(theme.palette.background.default, 0.6) : "background.paper",
                      backdropFilter: "blur(10px)",
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      {t("pages.landingDashboard.hero.benchmarkLabel")}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "success.main", mt: 0.5 }}>
                      {userSummary?.averageBenchmark ?? 0}%
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* 2. GRAPHICAL METRIC TILES */}
        <Grid container sx={{ mb: 4, gap: 3 }}>
          {/* Circular Accuracy Ring */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                p: 1,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    {t("pages.landingDashboard.metrics.accuracyScore")}
                  </Typography>
                  <Avatar
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: "primary.main", width: 36, height: 36 }}
                  >
                    <AssignmentTurnedIn fontSize="small" />
                  </Avatar>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 1 }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={110}
                      thickness={5}
                      sx={{ color: isDark ? alpha(theme.palette.divider, 0.2) : "#f1f5f9" }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={userSummary?.accuracy ?? 0}
                      size={110}
                      thickness={5}
                      sx={{
                        color: userSummary?.isPassing ? theme.palette.success.main : theme.palette.error.main,
                        position: "absolute",
                        left: 0,
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="h4" component="div" sx={{ fontWeight: 800, color: "text.primary" }}>
                        {userSummary?.accuracy ?? 0}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {t("pages.landingDashboard.metrics.correctCount", {
                          correctCount:
                            history30d?.history?.reduce((prev, current) => prev + current.stats.correctCount, 0) ?? 0,
                          totalQuestions:
                            history30d?.history?.reduce((prev, current) => prev + current.stats.totalQuestions, 0) ?? 0,
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Time & Velocity */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                p: 1,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    {t("pages.landingDashboard.metrics.velocityPace")}
                  </Typography>
                  <Avatar
                    sx={{ bgcolor: alpha(theme.palette.warning.main, 0.15), color: "warning.main", width: 36, height: 36 }}
                  >
                    <Speed fontSize="small" />
                  </Avatar>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 800, mt: 2, mb: 1, color: "text.primary" }}>
                  {history30d?.history?.length
                    ? history30d?.history?.reduce((prev, current) => prev + current.stats.avgPaceMin, 0) /
                      history30d?.history?.length
                    : 0}
                  <Typography component="span" variant="subtitle1" sx={{ color: "text.secondary" }}>
                    {t("pages.landingDashboard.metrics.minPerQ")}
                  </Typography>
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Stack sx={{ flexDirection: "row", mb: 0.5, justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {t("pages.landingDashboard.metrics.totalTime", { time: history30d?.summary.totalTimeSpentMin })}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "success.main" }}>
                      {t("pages.landingDashboard.metrics.optimalPace")}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={70}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: isDark ? alpha(theme.palette.divider, 0.2) : "#f1f5f9",
                      "& .MuiLinearProgress-bar": { bgcolor: "warning.main" },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Level */}
          {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                p: 1,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    {t("pages.landingDashboard.metrics.riskStatus")}
                  </Typography>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.15), color: "error.main", width: 36, height: 36 }}>
                    <ShieldOutlined fontSize="small" />
                  </Avatar>
                </Box>

                <Chip
                  icon={<Warning sx={{ fontSize: "16px !important" }} />}
                  label={t("pages.landingDashboard.metrics.riskAnomaly")}
                  color="warning"
                  sx={{ fontWeight: 700, mt: 1, mb: 2, borderRadius: 1 }}
                />

                <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                  {t("pages.landingDashboard.metrics.riskDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid> */}

          {/* Module Progress */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                p: 1,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    {t("pages.landingDashboard.metrics.currentModule")}
                  </Typography>
                  <Avatar
                    sx={{ bgcolor: alpha(theme.palette.success.main, 0.15), color: "success.main", width: 36, height: 36 }}
                  >
                    <School fontSize="small" />
                  </Avatar>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.2, mb: 1 }}>
                  {history30d?.history[0]?.quizInfo.paper ?? ""}
                </Typography>

                <Chip
                  label={t("pages.landingDashboard.metrics.sectionLabel")}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    borderRadius: 1,
                    borderColor: "divider",
                    color: "text.secondary",
                  }}
                />

                <Stack sx={{ flexDirection: "row", gap: 1, mt: 1, alignItems: "center" }}>
                  <CheckCircle sx={{ color: "success.main", fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    {t("pages.landingDashboard.metrics.attemptCompleted")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 3. CORE VISUAL MATRIX & QUICK ACTIONS */}
        <Grid container sx={{ gap: 3 }}>
          {/* Statutory Breakdown Bar Gauges */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                p: 2,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                height: "100%",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                      {t("pages.landingDashboard.coverage.title")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {t("pages.landingDashboard.coverage.subtitle")}
                    </Typography>
                  </Box>
                  <Tooltip title={t("pages.landingDashboard.coverage.tooltipRefresh")}>
                    <IconButton size="small" sx={{ color: "text.secondary" }}>
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Stack sx={{ gap: 3, my: 2 }}>
                  {/* HKMA BO Schedule 7 */}
                  {history30d?.history.map((_history) => (
                    <Box>
                      <Stack sx={{ flexDirection: "row", mb: 1, justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {_history.quizInfo.paper ?? ""}
                        </Typography>
                        <Chip
                          label={`${_history.stats.correctCount} / ${_history.stats.totalQuestions}`}
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 1 }}
                        />
                      </Stack>

                      <LinearProgress
                        variant="determinate"
                        value={(_history.stats.correctCount / _history.stats.totalQuestions) * 100}
                        // color={`${_history.stats.correctCount / _history.stats.totalQuestions < 0.5 ? "error" : _history.stats.correctCount / _history.stats.totalQuestions <= 0.7 ? "warning" : "success"}`}
                        color="error"
                        sx={{
                          height: 10,
                          borderRadius: 1,
                          bgcolor: isDark ? alpha(theme.palette.divider, 0.2) : "#e2e8f0",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: `${_history.stats.correctCount / _history.stats.totalQuestions < 0.5 ? "error.main" : _history.stats.correctCount / _history.stats.totalQuestions <= 0.7 ? "warning.main" : "success.main"}`,
                          },
                        }}
                      />
                    </Box>
                  ))}

                  {/* <Box>
                    <Stack sx={{ flexDirection: "row", mb: 1, justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {t("pages.landingDashboard.coverage.hkmaLabel")}
                      </Typography>
                      <Chip
                        label={t("pages.landingDashboard.coverage.hkmaBadge")}
                        color="success"
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: 1 }}
                      />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={history30d?.history[0].stats.overallScore}
                      sx={{
                        height: 10,
                        borderRadius: 1,
                        bgcolor: isDark ? alpha(theme.palette.divider, 0.2) : "#e2e8f0",
                        "& .MuiLinearProgress-bar": { bgcolor: "success.main" },
                      }}
                    />
                  </Box> 
                  <Box>
                    <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {t("pages.landingDashboard.coverage.amloLabel")}
                      </Typography>
                      <Chip
                        label={t("pages.landingDashboard.coverage.amloBadge")}
                        color="error"
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: 1 }}
                      />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={activeAttempt.complianceScores.amlo}
                      sx={{
                        height: 10,
                        borderRadius: 1,
                        bgcolor: isDark ? alpha(theme.palette.divider, 0.2) : "#e2e8f0",
                        "& .MuiLinearProgress-bar": { bgcolor: "error.main" },
                      }}
                    />
                  </Box>
 
                  <Box>
                    <Stack sx={{ flexDirection: "row", mb: 1, justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {t("pages.landingDashboard.coverage.oscoLabel")}
                      </Typography>
                      <Chip
                        label={t("pages.landingDashboard.coverage.oscoBadge")}
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: 1 }}
                      />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={activeAttempt.complianceScores.osco}
                      sx={{
                        height: 10,
                        borderRadius: 1,
                        bgcolor: isDark ? alpha(theme.palette.divider, 0.2) : "#e2e8f0",
                        "& .MuiLinearProgress-bar": { bgcolor: "warning.main" },
                      }}
                    />
                  </Box> */}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Action Hub */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack sx={{ gap: 3 }}>
              {/* Remediation Action Card */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 1,
                  p: 2,
                  bgcolor: isDark ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  boxShadow: "none",
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main", mb: 1 }}>
                    {t("pages.landingDashboard.remediation.title")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                    {t("pages.landingDashboard.remediation.desc")}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate("/revision/engine")}
                    sx={{
                      bgcolor: "primary.main",
                      "&:hover": { bgcolor: "primary.dark" },
                      borderRadius: 1,
                      fontWeight: 700,
                      textTransform: "none",
                    }}
                  >
                    {t("pages.landingDashboard.remediation.btnStart")}
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Metrics Widget */}
              {history30d?.history.map((_history) => (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 1,
                    p: 2,
                    bgcolor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: "text.primary" }}>
                      {t("pages.landingDashboard.statsSummary.title")}
                    </Typography>

                    <Stack sx={{ gap: 2 }} divider={<Divider flexItem sx={{ borderColor: "divider" }} />}>
                      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {t("pages.landingDashboard.statsSummary.completedQuestions")}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {t("pages.landingDashboard.statsSummary.questionsValue", { count: _history.stats.totalQuestions })}
                        </Typography>
                      </Stack>
                      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {t("pages.landingDashboard.statsSummary.avgTimePerSection")}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {t("pages.landingDashboard.statsSummary.minsValue", { count: _history.stats.timeConsumedMin })}
                        </Typography>
                      </Stack>
                      <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {t("pages.landingDashboard.statsSummary.historicalAvg")}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main" }}>
                          {`${(_history.stats.correctCount / _history.stats.totalQuestions) * 100}%`}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {/* <Card
                elevation={0}
                sx={{
                  borderRadius: 1,
                  p: 2,
                  bgcolor: "background.paper",
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.03)",
                }}
              >
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: "text.primary" }}>
                    {t("pages.landingDashboard.statsSummary.title")}
                  </Typography>

                  <Stack sx={{ gap: 2 }} divider={<Divider flexItem sx={{ borderColor: "divider" }} />}>
                    <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("pages.landingDashboard.statsSummary.completedQuestions")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {t("pages.landingDashboard.statsSummary.questionsValue", { count: activeAttempt.totalQuestions })}
                      </Typography>
                    </Stack>
                    <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("pages.landingDashboard.statsSummary.avgTimePerSection")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {t("pages.landingDashboard.statsSummary.minsValue", { count: activeAttempt.timeConsumedMin })}
                      </Typography>
                    </Stack>
                    <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("pages.landingDashboard.statsSummary.historicalAvg")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main" }}>
                        {t("pages.landingDashboard.statsSummary.successValue")}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card> */}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
